// EP-88 T108a — Mobile crash reporting + basic RUM (Analytics) for the
// Capacitor operator shell.
//
// Guard: EVERY call here is wrapped in `Capacitor.isNativePlatform()` so the
// module is completely inert on the desktop web build. This mirrors the pattern
// used in `lib/push/operatorPush.ts` (EP-77).
//
// Crash reporting: @capacitor-firebase/crashlytics — talks to the native
// Firebase Crashlytics SDK (not the web JS SDK), so this works only on iOS /
// Android. No-ops on the web platform.
//
// Analytics: @capacitor-firebase/analytics — same native-bridge approach.
//
// T108b coordination: T108b adds Firebase JS SDK init for the web browser
// analytics. This file is EXCLUSIVELY for native mobile. Do NOT call
// `getApp()` or any firebase-js-sdk import here — that would introduce a web
// Firebase dependency that T108b owns.
//
// Provisioning gate: The native plugins themselves will no-op or throw if the
// `GoogleService-Info.plist` (iOS) / `google-services.json` (Android) are
// absent. We catch and silence those errors so the app stays functional
// regardless.

import { Capacitor } from '@capacitor/core'
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics'
import { FirebaseAnalytics } from '@capacitor-firebase/analytics'

// ---------------------------------------------------------------------------
// Typed event catalogue for client analytics events
// ---------------------------------------------------------------------------

export type MobileAnalyticsEvent =
  | 'app_opened'
  | 'login_success'
  | 'login_failed'
  | 'logout_success'
  | 'invoice_viewed'
  | 'payment_started'
  | 'payment_success'
  | 'property_viewed'
  | 'maintenance_submitted'
  | 'screen_viewed'

// ---------------------------------------------------------------------------
// Initialise
// ---------------------------------------------------------------------------

let _initialised = false

/**
 * Initialise mobile crash + analytics. Call once after Capacitor is ready
 * (from `main.tsx` inside the `Capacitor.isNativePlatform()` block). Safe to
 * call multiple times — subsequent calls are no-ops.
 */
export async function initCrashlytics(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  if (_initialised) return
  _initialised = true

  try {
    // Enable crash collection. In development builds the plugin may not have
    // real Firebase credentials — it will silently fail in that case.
    await FirebaseCrashlytics.setEnabled({ enabled: true })
  } catch {
    /* best-effort: absent provisioning files produce a plugin error; ignore */
  }
}

// ---------------------------------------------------------------------------
// Crashlytics
// ---------------------------------------------------------------------------

/**
 * Record a JS exception to Crashlytics. Only fires on native platforms.
 * Non-fatal by default — pass `isFatal: true` only for unrecoverable errors.
 * Never throws.
 */
export async function recordCrash(
  error: unknown,
  {
    message,
    isFatal = false,
  }: { message?: string; isFatal?: boolean } = {},
): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  try {
    const msg =
      message ??
      (error instanceof Error ? error.message : String(error))
    await FirebaseCrashlytics.recordException({
      message: msg,
    })
    if (isFatal) {
      // Sending a second event tagged fatal signals a crash-severity event.
      await FirebaseCrashlytics.recordException({
        message: `FATAL: ${msg}`,
      })
    }
  } catch {
    /* best-effort */
  }
}

/**
 * Attach a custom key to future crash reports (e.g. `{ key: 'route', value: '/invoices' }`).
 * No-ops on web. Never throws.
 */
export async function setCrashKey(key: string, value: string): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  try {
    await FirebaseCrashlytics.setCustomKey({ key, value, type: 'string' })
  } catch {
    /* best-effort */
  }
}

// ---------------------------------------------------------------------------
// Analytics (RUM)
// ---------------------------------------------------------------------------

/**
 * Log an analytics event. No-ops on web. Never throws.
 *
 * T108b coordination: T108b handles web browser analytics via the Firebase JS
 * SDK. This function targets the native Firebase Analytics SDK only.
 */
export async function logMobileEvent(
  name: MobileAnalyticsEvent,
  params?: Record<string, string | number | boolean>,
): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  try {
    await FirebaseAnalytics.logEvent({
      name,
      params: params ?? {},
    })
  } catch {
    /* best-effort */
  }
}

/**
 * Log the current screen name. Call from the router `onNavigate` callback.
 * No-ops on web. Never throws.
 */
export async function logMobileScreenView(screenName: string): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  try {
    await FirebaseAnalytics.setCurrentScreen({ screenName })
  } catch {
    /* best-effort */
  }
}

// ---------------------------------------------------------------------------
// Test seam
// ---------------------------------------------------------------------------

/** Reset module state between tests. */
export function __resetCrashlyticsForTests(): void {
  _initialised = false
}
